// Super-admin-only API usage + cost dashboard.
//
// Big picture by default — total cost / call counts / cost by service /
// cost by user. Optionally drill into the raw recent-calls log.
//
// Gated on user.role === "super_admin". Anyone else just sees a 403 banner.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, CurrencyDollar, Database, ChartLine, Robot, MagnifyingGlass,
  ListBullets, Spinner, Bug, CheckCircle, XCircle,
} from "@phosphor-icons/react";
import { adminAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/app/BottomNav";

const WINDOWS = [
  { value: 1, label: "24 h" },
  { value: 7, label: "7 d" },
  { value: 30, label: "30 d" },
];

const SERVICE_META = {
  claude: { label: "Claude (vision + text)", color: "#000", icon: Robot },
  serpapi: { label: "SerpApi (sold comps)", color: "#FF6F00", icon: MagnifyingGlass },
  storage: { label: "Object Storage", color: "#1976D2", icon: Database },
};

export default function AdminUsagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [byUser, setByUser] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showRecent, setShowRecent] = useState(false);
  const [scraperStats, setScraperStats] = useState(null);
  const [scraperRecent, setScraperRecent] = useState([]);
  const [showScraperRecent, setShowScraperRecent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isAuthorized = user?.role === "super_admin";

  useEffect(() => {
    if (!isAuthorized) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([
      adminAPI.usageSummary(days),
      adminAPI.usageByUser(days, 25),
      adminAPI.usageTimeline(days),
      adminAPI.soldDataBreakdown(days),
    ])
      .then(([s, u, t, b]) => {
        if (cancelled) return;
        setSummary(s.data);
        setByUser(u.data.users || []);
        setTimeline(t.data.timeline || []);
        setScraperStats(b.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load usage metrics");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [days, isAuthorized]);

  const loadRecent = async () => {
    setShowRecent(true);
    if (recent.length > 0) return;
    try {
      const { data } = await adminAPI.usageRecent(100);
      setRecent(data.calls || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load recent calls");
    }
  };

  const loadScraperRecent = async () => {
    setShowScraperRecent(true);
    if (scraperRecent.length > 0) return;
    try {
      const { data } = await adminAPI.scraperRecent(100);
      setScraperRecent(data.calls || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load scraper calls");
    }
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-neutral-50 pb-24" data-testid="admin-usage-page">
        <Header onBack={() => navigate("/profile")} />
        <div className="p-6 max-w-md mx-auto">
          <div className="border-2 border-red-500 bg-red-50 p-4 font-body text-sm" data-testid="admin-usage-forbidden">
            403 — Super admin access required. Reach out if you think this is a mistake.
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="admin-usage-page">
      <Header onBack={() => navigate("/profile")} />

      <div className="p-6 space-y-6 max-w-3xl mx-auto">
        {/* Window selector */}
        <div className="flex items-center gap-2">
          <span className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500">Window:</span>
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setDays(w.value)}
              className={`px-3 py-1.5 font-heading text-xs font-bold uppercase tracking-wider border-2 border-black ${
                days === w.value ? "bg-black text-white" : "bg-white text-black"
              }`}
              data-testid={`admin-usage-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="border-2 border-red-500 bg-red-50 p-3 font-body text-sm text-red-700" data-testid="admin-usage-error">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={28} weight="bold" className="animate-spin" />
          </div>
        )}

        {!loading && summary && (
          <>
            {/* Big numbers */}
            <div className="grid grid-cols-2 gap-3">
              <BigStat
                label={`Total Cost · ${days}d`}
                value={`$${summary.total_cost_usd.toFixed(2)}`}
                icon={CurrencyDollar}
                testid="admin-usage-total-cost"
              />
              <BigStat
                label={`Total Calls · ${days}d`}
                value={summary.total_calls.toLocaleString()}
                icon={ChartLine}
                testid="admin-usage-total-calls"
              />
              <BigStat
                label="Active Users"
                value={summary.active_users}
                testid="admin-usage-active-users"
              />
              <BigStat
                label="Cost / Active User"
                value={`$${summary.avg_cost_per_active_user_usd.toFixed(2)}`}
                testid="admin-usage-cost-per-user"
              />
            </div>

            {/* By service */}
            <Section title="Cost by Service">
              <div className="space-y-2">
                {summary.by_service.length === 0 && (
                  <p className="font-body text-sm text-neutral-500">No usage logged yet.</p>
                )}
                {summary.by_service.map((s) => {
                  const meta = SERVICE_META[s.service] || { label: s.service, color: "#666", icon: Database };
                  const Icon = meta.icon;
                  return (
                    <div
                      key={s.service}
                      className="border-2 border-black bg-white p-3 flex items-center gap-3"
                      data-testid={`admin-usage-service-${s.service}`}
                    >
                      <div className="w-9 h-9 border-2 border-black flex items-center justify-center shrink-0" style={{ background: meta.color, color: "#fff" }}>
                        <Icon size={18} weight="bold" />
                      </div>
                      <div className="flex-1">
                        <div className="font-heading text-sm font-bold uppercase tracking-tight">{meta.label}</div>
                        <div className="font-body text-[11px] text-neutral-500">
                          {s.calls.toLocaleString()} calls
                          {s.service === "claude" && s.image_count > 0 && ` · ${s.image_count.toLocaleString()} images`}
                          {s.service === "claude" && ` · ${(s.input_tokens / 1000).toFixed(0)}k in / ${(s.output_tokens / 1000).toFixed(0)}k out`}
                          {s.service === "storage" && s.bytes_count > 0 && ` · ${(s.bytes_count / (1024 * 1024)).toFixed(1)} MB`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading text-base font-black">${s.cost_usd.toFixed(2)}</div>
                        <div className="font-body text-[10px] text-neutral-400">
                          ${(s.cost_usd / Math.max(s.calls, 1)).toFixed(4)} / call
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Timeline */}
            {timeline.length > 0 && (
              <Section title="Daily Trend">
                <TimelineChart data={timeline} />
              </Section>
            )}

            {/* Scraper vs SerpApi breakdown */}
            {scraperStats && (
              <Section title="Sold-data: Scraper vs SerpApi">
                <ScraperStats
                  stats={scraperStats}
                  showRecent={showScraperRecent}
                  recent={scraperRecent}
                  onLoadRecent={loadScraperRecent}
                />
              </Section>
            )}

            {/* By user */}
            <Section title="Top Users by Cost">
              <div className="border-2 border-black bg-white overflow-hidden">
                <div className="grid grid-cols-12 px-3 py-2 bg-black text-white font-heading text-[10px] font-bold uppercase tracking-wider">
                  <div className="col-span-6">User</div>
                  <div className="col-span-2 text-right">Calls</div>
                  <div className="col-span-2 text-right">Cost</div>
                  <div className="col-span-2 text-right">Last seen</div>
                </div>
                {byUser.length === 0 && (
                  <div className="px-3 py-6 font-body text-sm text-neutral-500 text-center">
                    No usage logged yet.
                  </div>
                )}
                {byUser.map((u) => (
                  <div
                    key={u.user_id}
                    className="grid grid-cols-12 px-3 py-2 border-t-2 border-neutral-200 font-body text-xs"
                    data-testid={`admin-usage-user-${u.user_id}`}
                  >
                    <div className="col-span-6 truncate">
                      <div className="font-semibold">{u.email}</div>
                      {u.name && <div className="text-[10px] text-neutral-500">{u.name} · {u.role}</div>}
                    </div>
                    <div className="col-span-2 text-right">{u.calls}</div>
                    <div className="col-span-2 text-right font-heading font-bold">${u.cost_usd.toFixed(3)}</div>
                    <div className="col-span-2 text-right text-[10px] text-neutral-500 truncate">
                      {u.last_seen ? u.last_seen.split("T")[0] : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* Recent calls drilldown */}
            <Section title="Recent Calls (drill-down)">
              {!showRecent ? (
                <button
                  onClick={loadRecent}
                  className="w-full bg-white text-black p-3 border-2 border-black font-heading text-xs font-bold uppercase tracking-widest hover:bg-neutral-100 flex items-center justify-center gap-2"
                  data-testid="admin-usage-show-recent"
                >
                  <ListBullets size={16} weight="bold" /> Load Last 100 Calls
                </button>
              ) : (
                <RecentTable rows={recent} />
              )}
            </Section>
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

// --------- Sub-components ---------

function Header({ onBack }) {
  return (
    <div className="sticky top-0 z-40 bg-white border-b-4 border-black py-4 px-5 flex items-center gap-3">
      <button onClick={onBack} className="p-1.5 border-2 border-black hover:bg-neutral-100" data-testid="admin-usage-back">
        <ArrowLeft size={18} weight="bold" />
      </button>
      <h1 className="font-heading text-xl font-black uppercase tracking-tight">API Usage & Cost</h1>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-heading text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function BigStat({ label, value, icon: Icon, testid }) {
  return (
    <div className="border-2 border-black bg-white p-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} weight="bold" className="text-neutral-400" />}
        <span className="font-heading text-[10px] font-bold uppercase tracking-widest text-neutral-500">{label}</span>
      </div>
      <div className="font-heading text-2xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function ScraperStats({ stats, showRecent, recent, onLoadRecent }) {
  const s = stats.scraper || {};
  const sp = stats.serpapi || {};
  const successRate = s.success_rate_pct;
  const proxied = stats.scraper_proxy_enabled;
  const mode = stats.backend_mode;
  return (
    <div className="space-y-3" data-testid="admin-usage-scraper-stats">
      {/* Backend mode + proxy status */}
      <div className="flex flex-wrap gap-2 text-[10px]">
        <span className="px-2 py-1 border-2 border-black bg-white font-heading font-bold uppercase tracking-wider">
          Backend: {mode}
        </span>
        <span className={`px-2 py-1 border-2 border-black font-heading font-bold uppercase tracking-wider ${proxied ? "bg-[#00E676]" : "bg-amber-200"}`}>
          Proxy: {proxied ? "On" : "Off"}
        </span>
        {!proxied && (
          <span className="px-2 py-1 font-body text-[10px] italic text-neutral-500">
            Set <code className="font-mono bg-neutral-100 px-1">EBAY_SCRAPER_PROXY_URL</code> to enable savings
          </span>
        )}
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-2">
        <div className="border-2 border-black bg-white p-3" data-testid="admin-usage-scraper-success-rate">
          <div className="flex items-center gap-1.5 mb-1">
            <Bug size={14} weight="bold" />
            <span className="font-heading text-[10px] font-bold uppercase tracking-wider text-neutral-500">Scraper Success</span>
          </div>
          <div className="font-heading text-2xl font-black">
            {successRate !== null && successRate !== undefined ? `${successRate}%` : "—"}
          </div>
          <div className="font-body text-[10px] text-neutral-500 mt-0.5">
            {s.success_calls || 0} ok · {s.failed_calls || 0} blocked / failed
          </div>
        </div>
        <div className="border-2 border-black bg-white p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <CurrencyDollar size={14} weight="bold" />
            <span className="font-heading text-[10px] font-bold uppercase tracking-wider text-neutral-500">SerpApi Fallback</span>
          </div>
          <div className="font-heading text-2xl font-black">
            ${sp.cost_usd?.toFixed(2) ?? "0.00"}
          </div>
          <div className="font-body text-[10px] text-neutral-500 mt-0.5">
            {sp.success_calls || 0} succeeded · {sp.failed_calls || 0} errored
          </div>
        </div>
      </div>

      {/* Savings tile */}
      <div className={`border-2 border-black p-3 ${stats.savings_usd_vs_all_serpapi > 0 ? "bg-[#00E676]" : "bg-neutral-100"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-[10px] font-bold uppercase tracking-wider">Savings vs. all-SerpApi</div>
            <div className="font-body text-[10px] text-neutral-700">
              What you'd have paid if every scraper-success call had gone to SerpApi instead.
            </div>
          </div>
          <div className="font-heading text-2xl font-black" data-testid="admin-usage-savings">
            ${(stats.savings_usd_vs_all_serpapi || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Latency */}
      {s.avg_latency_ms && (
        <div className="font-body text-[11px] text-neutral-500">
          Avg scraper latency: {Math.round(s.avg_latency_ms)} ms
        </div>
      )}

      {/* Drill-down */}
      {!showRecent ? (
        <button
          onClick={onLoadRecent}
          className="w-full bg-white text-black p-2.5 border-2 border-black font-heading text-xs font-bold uppercase tracking-widest hover:bg-neutral-100 flex items-center justify-center gap-2"
          data-testid="admin-usage-show-scraper-recent"
        >
          <ListBullets size={14} weight="bold" /> Last 100 Scraper Attempts
        </button>
      ) : (
        <div className="border-2 border-black bg-white overflow-x-auto">
          {recent.length === 0 ? (
            <div className="p-4 font-body text-sm text-neutral-500 text-center">No scraper attempts yet.</div>
          ) : (
            <table className="w-full text-xs font-body">
              <thead className="bg-black text-white font-heading text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-1.5 text-left">Time</th>
                  <th className="px-2 py-1.5 text-left">Query</th>
                  <th className="px-2 py-1.5">OK?</th>
                  <th className="px-2 py-1.5 text-right">Items</th>
                  <th className="px-2 py-1.5 text-right">Latency</th>
                  <th className="px-2 py-1.5">Proxy</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-200" data-testid={`admin-usage-scraper-row-${i}`}>
                    <td className="px-2 py-1 text-[10px] text-neutral-500">
                      {(r.created_at || "").split("T")[1]?.slice(0, 8)}
                    </td>
                    <td className="px-2 py-1 truncate max-w-[180px]">{r.query || "—"}</td>
                    <td className="px-2 py-1 text-center">
                      {r.success ? (
                        <CheckCircle size={14} weight="bold" className="inline text-green-600" />
                      ) : (
                        <XCircle size={14} weight="bold" className="inline text-red-500" />
                      )}
                    </td>
                    <td className="px-2 py-1 text-right">{r.result_count || 0}</td>
                    <td className="px-2 py-1 text-right text-[10px] text-neutral-500">{r.latency_ms || 0}ms</td>
                    <td className="px-2 py-1 text-center text-[10px]">{r.proxied ? "✓" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


function TimelineChart({ data }) {
  const max = useMemo(() => Math.max(0.01, ...data.map((d) => d.total_cost)), [data]);
  return (
    <div className="border-2 border-black bg-white p-3" data-testid="admin-usage-timeline-chart">
      <div className="flex items-end justify-between h-32 gap-1">
        {data.map((d) => {
          const pct = (d.total_cost / max) * 100;
          return (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-1">
              <div className="font-body text-[8px] text-neutral-400">${d.total_cost.toFixed(2)}</div>
              <div className="w-full bg-black" style={{ height: `${Math.max(2, pct)}%` }} title={`${d.day}: $${d.total_cost.toFixed(2)}`} />
              <div className="font-body text-[9px] text-neutral-500 leading-tight">
                {d.day.slice(5)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecentTable({ rows }) {
  if (rows.length === 0) {
    return <div className="font-body text-sm text-neutral-500">No calls logged yet.</div>;
  }
  return (
    <div className="border-2 border-black bg-white overflow-x-auto">
      <table className="w-full text-xs font-body">
        <thead className="bg-black text-white font-heading text-[10px] uppercase tracking-wider">
          <tr>
            <th className="px-2 py-1.5 text-left">Time</th>
            <th className="px-2 py-1.5 text-left">User</th>
            <th className="px-2 py-1.5 text-left">Service</th>
            <th className="px-2 py-1.5 text-left">Operation</th>
            <th className="px-2 py-1.5 text-right">Tokens</th>
            <th className="px-2 py-1.5 text-right">Cost</th>
            <th className="px-2 py-1.5 text-right">Latency</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-neutral-200" data-testid={`admin-usage-recent-row-${i}`}>
              <td className="px-2 py-1 text-[10px] text-neutral-500">{(r.created_at || "").split("T")[1]?.slice(0, 8)}</td>
              <td className="px-2 py-1 truncate max-w-[140px]">{r.email || "—"}</td>
              <td className="px-2 py-1 font-semibold">{r.service}</td>
              <td className="px-2 py-1 text-neutral-600">{r.operation}</td>
              <td className="px-2 py-1 text-right text-neutral-500">
                {r.input_tokens || r.output_tokens
                  ? `${r.input_tokens || 0}/${r.output_tokens || 0}`
                  : "—"}
              </td>
              <td className="px-2 py-1 text-right font-bold">${(r.cost_usd || 0).toFixed(4)}</td>
              <td className="px-2 py-1 text-right text-[10px] text-neutral-500">{r.latency_ms || 0}ms</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
